import SearchIcon from "@mui/icons-material/Search";
import { Box, IconButton, TextField, Typography } from "@mui/material";
import React, { Dispatch, SetStateAction } from "react";
import { FormContainer, useForm } from "react-hook-form-mui";
import { useNavigate } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useAuth } from "src/shared/context/useAuth";
import { blockNonDigit, validateBigIntRange } from "src/shared/utils";

type SearchMode =
  | "application"
  | "certificate"
  | "user"
  | "complaint"
  | "violationCase"
  | "sampleCase";

type SearchBarProps = {
  mode: SearchMode;
  protocol: string;
  gemi: string;
  setProtocol: Dispatch<SetStateAction<string>>;
  setGemi: Dispatch<SetStateAction<string>>;
};

type RoutedMode = Exclude<SearchMode, "complaint">;

const ROUTES: Record<
  RoutedMode,
  {
    byProtocol: (protocol: string) => string;
    byGemi: (gemi: string) => string;
  }
> = {
  application: {
    byProtocol: (protocol) => `/chamber/manageApplication?appId=${protocol}`,
    byGemi: (gemi) => `/chamber/getAppsByGemi?gemiAppId=${gemi}`,
  },
  certificate: {
    byProtocol: (protocol) =>
      `/chamber/manageCertificateApplication?certId=${protocol}`,
    byGemi: (gemi) => `/chamber/getCertsByGemi?gemiId=${gemi}`,
  },
  user: {
    byProtocol: (protocol) => `/beneficiary/manageApplication/${protocol}`,
    byGemi: (gemi) =>
      `/beneficiary/representativesRequestsForChamber?mode=gemiNum&gemiNum=${gemi}`,
  },
  violationCase: {
    byProtocol: (protocol) => `/violation/case/${protocol}/details`,
    byGemi: (gemi) => `/violation/chamber/casesDashboardByCompany/${gemi}`,
  },
};

type Preset =
  | { kind: "protocol"; value: string }
  | { kind: "gemi"; value: string };

const MODE_LABELS: Record<
  SearchMode,
  { title: string; protocolPlaceholder: string }
> = {
  application: {
    title: "Αναζήτηση με Αριθμό Πρωτοκόλλου ή Γ.Ε.ΜΗ.",
    protocolPlaceholder: "Αριθμός Πρωτοκόλλου",
  },
  certificate: {
    title: "Αναζήτηση με Αριθμό Πρωτοκόλλου ή Γ.Ε.ΜΗ.",
    protocolPlaceholder: "Αριθμός Πρωτοκόλλου",
  },
  user: {
    title: "Αναζήτηση με Αριθμό Πρωτοκόλλου Αιτήματος ή Γ.Ε.ΜΗ.",
    protocolPlaceholder: "Αριθμός Πρωτοκόλλου Αιτήματος",
  },
  complaint: {
    title: "Αναζήτηση με Αριθμό Υπόθεσης ή Γ.Ε.ΜΗ.",
    protocolPlaceholder: "Αριθμός Υπόθεσης",
  },
  violationCase: {
    title: "Αναζήτηση με Αριθμό Υπόθεσης ή Γ.Ε.ΜΗ.",
    protocolPlaceholder: "Αριθμός Υπόθεσης",
  },
  sampleCase: {
    title: "Αναζήτηση με Αριθμό Υπόθεσης ή Γ.Ε.ΜΗ.",
    protocolPlaceholder: "Αριθμός Υπόθεσης",
  },
};

export function SearchBar({
  mode,
  protocol,
  gemi,
  setProtocol,
  setGemi,
}: SearchBarProps) {
  const { t } = useTranslation();
  const formContext = useForm();
  const navigate = useNavigate();
  const { isSampleInitiator } = useAuth();
  const labels = MODE_LABELS[mode];

  const [protocolError, setProtocolError] = React.useState<string | null>(null);
  const [gemiError, setGemiError] = React.useState<string | null>(null);

  const makeNumericChange =
    (setter: Dispatch<SetStateAction<string>>, setError: (e: string | null) => void) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value.replace(/\D/g, "");
      setter(val);
      const result = validateBigIntRange(val);
      setError(result === true ? null : result);
    };

  const hasProtocol = protocol.trim().length > 0;
  const hasGemi = gemi.trim().length > 0;

  const makePreset = (p: string, g: string): Preset | null => {
    if (p) return { kind: "protocol", value: p };
    if (g) return { kind: "gemi", value: g };
    return null;
  };

  const handleSearch = () => {
    if (protocolError || gemiError) return;
    const p = protocol.trim();
    const g = gemi.trim();
    const preset = makePreset(p, g);
    if (!preset) return;

    if (mode === "complaint") {
      navigate("/complaint/activeComplaintApplications", { state: { preset } });
      return;
    }

    if (mode === "sampleCase") {
      if (preset.kind === "protocol") {
        navigate(`/sampleInspection/case/${preset.value}`);
      } else {
        navigate("/sampleInspection/case/dashboard", {
          state: { companyId: preset.value },
        });
      }
      return;
    }

    preset.kind === "protocol"
      ? navigate(ROUTES[mode].byProtocol(preset.value))
      : navigate(ROUTES[mode].byGemi(preset.value));
  };

  return (
    <Box
      sx={{
        px: 2,
        pb: 1.5,
        backgroundColor: "#F0F8FF",
        borderRadius: 2,
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <Typography sx={{ mb: 1 }}>{t(labels.title)}</Typography>
      <FormContainer
        onSuccess={handleSearch}
        onError={(formError) => {
          Object.entries(formError).forEach(([key, value]) => {
            value?.message
              ? enqueueSnackbar(value?.message, { variant: "error" })
              : null;
          });
        }}
        formContext={formContext}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            width: "100%",
            flexWrap: { xs: "wrap", md: "nowrap" },
          }}
        >
          <TextField
            value={protocol}
            onChange={makeNumericChange(setProtocol, setProtocolError)}
            onKeyDown={blockNonDigit}
            placeholder={t(labels.protocolPlaceholder)}
            size="small"
            disabled={hasGemi}
            error={!!protocolError}
            helperText={protocolError ?? undefined}
            inputProps={{ inputMode: "numeric" }}
            sx={{
              flex: "1 1 260px",
              minWidth: 0,
            }}
            fullWidth
          />

          <TextField
            value={gemi}
            onChange={makeNumericChange(setGemi, setGemiError)}
            onKeyDown={blockNonDigit}
            placeholder={t("Αριθμός Γ.Ε.ΜΗ.")}
            size="small"
            disabled={hasProtocol}
            error={!!gemiError}
            helperText={gemiError ?? undefined}
            inputProps={{ inputMode: "numeric" }}
            sx={{
              flex: "1 1 260px",
              minWidth: 0,
            }}
            fullWidth
          />

          <IconButton
            type="submit"
            disabled={!hasProtocol && !hasGemi}
            sx={{
              flex: "0 0 auto",
              width: 44,
              height: 44,
              borderRadius: 2,
              bgcolor: "primary.main",
              color: "primary.contrastText",
              "&:hover": { bgcolor: "primary.dark" },
            }}
          >
            <SearchIcon />
          </IconButton>
        </Box>
      </FormContainer>
    </Box>
  );
}
